import React, { useState, useEffect, useRef } from 'react';
import { api } from '../../utils/api';
import { Save, Plus, Trash2 } from 'lucide-react';
import ImageUploadField from './ImageUploadField';
import SectionsEditor from './SectionsEditor';
import { useTranscodeJobs, StreamingBadge } from './StreamingStatus';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FilmLandingPanel = () => {
  const [content, setContent] = useState({
    hero_video_url: '',
    hero_fallback_image_url: '',
    hero_heading: 'Crystal Films',
    hero_subheading: 'Cinematic wedding films that tell your story',
    process_heading: 'Our Process',
    process_subheading: 'From consultation to final delivery, every step is intentional',
    process_steps: [],
    sections: [],
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [uploading, setUploading] = useState(false);
  const videoInputRef = useRef(null);

  const { jobs: heroJobs, refresh: refreshHeroJobs } = useTranscodeJobs();
  const prepareHero = async (maxHeight) => {
    try {
      await api.request(`/api/admin/film-landing/transcode-hero${maxHeight ? `?max_height=${maxHeight}` : ''}`, { method: 'POST' });
      refreshHeroJobs();
    } catch (e) { alert('Failed: ' + e.message); }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await fetch(`${API_URL}/api/content/film-landing`);
        const data = await res.json();
        if (data) setContent(prev => ({ ...prev, ...data }));
      } catch (e) {
        console.error('Failed to load film landing content:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const save = async () => {
    setSaving(true);
    setMessage('');
    try {
      await api.updateFilmLandingContent(content);
      setMessage('Film landing content saved!');
      setTimeout(() => setMessage(''), 3000);
    } catch (e) {
      setMessage('Error: ' + e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleVideoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const result = await api.uploadVideo(file, (percent) => {
        setMessage(`Uploading video: ${percent}%`);
      });
      setContent(prev => ({ ...prev, hero_video_url: result.url }));
      setMessage('Video uploaded!');
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      setMessage('Error: ' + err.message);
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const updateStep = (index, field, value) => {
    const updated = { ...content };
    updated.process_steps[index][field] = value;
    setContent(updated);
  };

  const addStep = () => {
    const steps = content.process_steps || [];
    const num = String(steps.length + 1).padStart(2, '0');
    setContent({
      ...content,
      process_steps: [...steps, { number: num, title: '', description: '', order: steps.length }],
    });
  };

  const removeStep = (index) => {
    const updated = { ...content };
    updated.process_steps.splice(index, 1);
    setContent(updated);
  };

  if (loading) return <div className="py-12 text-center text-gray-500">Loading...</div>;

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-bold">Film Landing Page</h2>
        <p className="text-sm text-gray-500 mt-1">Manage the Film section landing page content</p>
      </div>

      {message && (
        <div className={`p-3 rounded text-sm ${message.includes('Error') ? 'bg-red-50 text-red-800' : 'bg-green-50 text-green-800'}`}>{message}</div>
      )}

      {/* Hero Section */}
      <div className="border rounded-lg p-6 space-y-5">
        <h3 className="text-lg font-semibold border-b pb-3">Hero Section</h3>

        {/* Hero Video */}
        <div>
          <label className="block text-sm font-medium mb-2">Hero Background Video (loops)</label>
          {content.hero_video_url ? (
            <div className="flex items-center gap-4 p-3 bg-green-50 border border-green-200 rounded">
              <video src={content.hero_video_url} className="h-16 w-28 object-cover rounded" muted autoPlay loop playsInline />
              <div className="flex-1">
                <p className="text-sm text-green-800">Video uploaded</p>
                <div className="mt-1">
                  <StreamingBadge target={{ id: 'hero', hero_video_url: content.hero_video_url, hero_hls_url: content.hero_hls_url }} job={heroJobs.film_landing_hero} onPrepare={prepareHero} />
                </div>
              </div>
              <div className="flex gap-2">
                <button onClick={() => videoInputRef.current?.click()} disabled={uploading} className="px-3 py-1 text-sm border rounded hover:bg-white">Replace</button>
                <button onClick={() => setContent({ ...content, hero_video_url: '' })} className="px-3 py-1 text-sm text-red-600 border border-red-200 rounded hover:bg-red-50">Remove</button>
              </div>
            </div>
          ) : (
            <div onClick={() => videoInputRef.current?.click()} className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:border-gray-400 transition-colors">
              <p className="text-sm text-gray-600">{uploading ? 'Uploading...' : 'Click to upload hero video'}</p>
              <p className="text-xs text-gray-400 mt-1">MP4, MOV, WebM (will loop as background)</p>
            </div>
          )}
          <input ref={videoInputRef} type="file" accept="video/*" onChange={handleVideoUpload} className="hidden" />
        </div>

        {/* Fallback Image */}
        <ImageUploadField
          value={content.hero_fallback_image_url}
          onChange={(url) => setContent({ ...content, hero_fallback_image_url: url })}
          folder="film-landing"
          label="Fallback Image (shown if no video or while loading)"
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Heading</label>
            <input type="text" value={content.hero_heading} onChange={(e) => setContent({ ...content, hero_heading: e.target.value })} className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-black" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Subheading</label>
            <input type="text" value={content.hero_subheading} onChange={(e) => setContent({ ...content, hero_subheading: e.target.value })} className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-black" />
          </div>
        </div>
      </div>

      {/* Our Process Section */}
      <div className="border rounded-lg p-6 space-y-5">
        <div className="flex items-center justify-between border-b pb-3">
          <h3 className="text-lg font-semibold">Our Process</h3>
          <button onClick={addStep} className="flex items-center gap-1 px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded transition-colors">
            <Plus size={14} /> Add Step
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Section Heading</label>
            <input type="text" value={content.process_heading} onChange={(e) => setContent({ ...content, process_heading: e.target.value })} className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-black" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Section Subheading</label>
            <input type="text" value={content.process_subheading} onChange={(e) => setContent({ ...content, process_subheading: e.target.value })} className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-black" />
          </div>
        </div>

        <div className="space-y-3">
          {(content.process_steps || []).map((step, i) => (
            <div key={i} className="p-4 bg-gray-50 rounded space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-500">Step {step.number}</span>
                <button onClick={() => removeStep(i)} className="p-1 text-red-400 hover:text-red-600"><Trash2 size={14} /></button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Number</label>
                  <input type="text" value={step.number} onChange={(e) => updateStep(i, 'number', e.target.value)} className="w-full px-2 py-1.5 border rounded text-sm" />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-xs text-gray-500 mb-1">Title</label>
                  <input type="text" value={step.title} onChange={(e) => updateStep(i, 'title', e.target.value)} className="w-full px-2 py-1.5 border rounded text-sm" />
                </div>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Description</label>
                <textarea value={step.description} onChange={(e) => updateStep(i, 'description', e.target.value)} rows={2} className="w-full px-2 py-1.5 border rounded text-sm" />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Content Sections */}
      <div className="border rounded-lg p-6">
        <SectionsEditor
          sections={content.sections || []}
          onChange={(sections) => setContent({ ...content, sections })}
          folder="film-landing"
        />
      </div>

      {/* Save */}
      <div className="flex justify-end">
        <button onClick={save} disabled={saving} className="px-6 py-2 bg-black text-white text-sm rounded hover:bg-gray-800 disabled:opacity-50 flex items-center gap-2" data-testid="save-film-landing">
          <Save size={14} />{saving ? 'Saving...' : 'Save Film Landing'}
        </button>
      </div>
    </div>
  );
};

export default FilmLandingPanel;
